// src/web/client/src/components/messages/NewTemplateDialog.jsx
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { API_BASE_URL } from "@/constants";
import { Sparkles, Wand2, Info } from "lucide-react";
import { authenticatedFetch } from "@/utils/authenticatedFetch";

// When the merchant picks a message type but hasn't explicitly touched the
// trigger dropdown, jump to the trigger that makes sense for that type:
//   review_request → sent AFTER the order is delivered (otherwise the customer
//                    gets asked to review a product that hasn't arrived yet —
//                    the exact issue we just debugged in dev2)
//   vip_upsell     → returning customers (a "VIP" upsell to a first-time buyer
//                    is just an upsell)
//   custom_offer   → triggered by a high-value purchase by default
//   thank_you      → on order placement, as it already was
// If the merchant has explicitly picked a trigger, we respect that and don't
// touch it on subsequent type changes.
const NATURAL_TRIGGER_BY_TYPE = {
  thank_you: "order_placed",
  review_request: "order_delivered",
  vip_upsell: "repeat_customer",
  custom_offer: "high_value_purchase",
};

// `template` (optional, additive like initialType): when passed, the dialog is
// an EDIT form for that template — prefilled values, locked type (a template's
// type is its slot identity in the 4-card menu), edit wording. When absent,
// behavior is byte-identical to the original create flow. The dialog itself
// never decides POST vs PUT — it returns form data; the PARENT routes it.
export default function NewTemplateDialog({ open, onClose, onSave, initialType = "thank_you", template = null }) {
  const { toast } = useToast();
  const isEdit = !!template;
  const [shop, setShop] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [useAI, setUseAI] = React.useState(true);
  const [formData, setFormData] = React.useState({
    name: "",
    // Opens on the type the caller asked for (a placeholder's "Set up"); the
    // trigger follows the natural default for that type. Defaults preserve the
    // original thank_you behaviour when no initialType is passed.
    type: initialType,
    subject: "Thank you for your order, {{customer_name}}!",
    content: "",
    preview: "",
    prompt: "",
    trigger: NATURAL_TRIGGER_BY_TYPE[initialType] || "order_placed",
    is_active: true
  });
  // Tracks whether the merchant has explicitly picked a trigger from the
  // dropdown. Once they have, type changes won't overwrite it.
  const [userPickedTrigger, setUserPickedTrigger] = React.useState(false);

  const typeDescriptions = {
    thank_you: {
      title: "Thank You Email",
      description: "Sent right after purchase to make customers feel appreciated",
      aiExample: "Our AI will write a warm, personalized thank you that mentions their name and makes them feel valued."
    },
    review_request: {
      title: "Review Request",
      description: "Sent after delivery to encourage feedback",
      aiExample: "Our AI will craft a friendly, non-pushy request that makes leaving a review feel easy and meaningful."
    },
    vip_upsell: {
      title: "VIP Upsell",
      description: "Special offers for your best customers",
      aiExample: "Our AI will create an exclusive message that makes VIP customers feel special and appreciated."
    },
    custom_offer: {
      title: "Custom Offer",
      description: "Personalized promotions based on purchase history",
      aiExample: "Our AI will generate a relevant, timely offer that feels personal, not spammy."
    }
  };

  const triggerDescriptions = {
    order_placed: "Immediately after checkout",
    order_delivered: "When order is marked delivered",
    high_value_purchase: "Orders above a certain amount",
    repeat_customer: "After their 2nd+ purchase"
  };

  React.useEffect(() => {
    if (open) {
      loadShop();
      resetForm();
    }
    // template in deps: reopening on a different card must prefill that card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template]);

  const resetForm = () => {
    if (template) {
      // EDIT mode — prefill from the real template. is_active is carried but
      // never sent on update (activation belongs to the card's toggle alone).
      setFormData({
        name: template.name || "",
        type: template.type,
        subject: template.subject || "",
        content: template.content || "",
        preview: template.preview || "",
        prompt: template.prompt || "",
        trigger: template.trigger || NATURAL_TRIGGER_BY_TYPE[template.type] || "order_placed",
        is_active: !!template.is_active
      });
      // Existing content → the merchant wrote their own; empty → AI-generated.
      setUseAI(!template.content);
      // The stored trigger is the merchant's earlier choice — never auto-swap it.
      setUserPickedTrigger(true);
      return;
    }
    setFormData({
      name: "",
      type: initialType,
      subject: "Thank you for your order, {{customer_name}}!",
      content: "",
      preview: "",
      prompt: "",
      trigger: NATURAL_TRIGGER_BY_TYPE[initialType] || "order_placed",
      is_active: true
    });
    setUseAI(true);
    setUserPickedTrigger(false);
  };

  // Changing the message type pulls the trigger to the natural default for
  // that type — but only if the merchant hasn't explicitly chosen one yet.
  const handleTypeChange = (value) => {
    setFormData((prev) => {
      const next = { ...prev, type: value };
      if (!userPickedTrigger) {
        next.trigger = NATURAL_TRIGGER_BY_TYPE[value] || prev.trigger;
      }
      return next;
    });
  };

  // Any explicit trigger pick locks the trigger in — future type changes
  // won't overwrite it.
  const handleTriggerChange = (value) => {
    setUserPickedTrigger(true);
    setFormData((prev) => ({ ...prev, trigger: value }));
  };

  const loadShop = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const shopDomain = params.get("shop") || params.get("shop_domain") || 
        localStorage.getItem("shop_domain") || localStorage.getItem("shopDomain");

      const url = shopDomain
        ? `${API_BASE_URL}/shops?shop_domain=${encodeURIComponent(shopDomain)}`
        : `${API_BASE_URL}/shops`;

        const res = await authenticatedFetch(url);

      const data = await res.json();

      let shopRecord = null;
      if (Array.isArray(data) && data.length > 0) {
        shopRecord = data[0];
      } else if (data && Array.isArray(data.shops) && data.shops.length > 0) {
        shopRecord = data.shops[0];
      } else if (data && data.shop) {
        shopRecord = data.shop;
      }

      if (shopRecord) {
        setShop(shopRecord);
      } else {
        toast({
          title: "No store found",
          description: "Please set up your store first in Store Settings",
          variant: "destructive",
        });
        if (typeof onClose === "function") onClose();
      }
    } catch (error) {
      console.error("Error loading shop:", error);
      toast({
        title: "Error",
        description: "Failed to load store information",
        variant: "destructive",
      });
    }
    setIsLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!shop) {
      toast({
        title: "No store found",
        description: "Please set up your store first in Store Settings",
        variant: "destructive",
      });
      return;
    }
    
    const dataToSave = {
      ...formData,
      shop_id: shop.id,
      content: useAI ? "" : formData.content // Empty content = AI will generate
    };
    
    onSave(dataToSave);
  };

  const currentType = typeDescriptions[formData.type];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            {isEdit ? "Template settings" : "Create AI-Powered Message"}
          </DialogTitle>
          <DialogDescription className="text-base">
            {isEdit
              ? "Changes apply to the next emails sent from this template."
              : "Just choose when to send - our AI writes personalized emails for each customer automatically."}
          </DialogDescription>
        </DialogHeader>

        {/* AI Feature Highlight */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg p-4 my-2">
          <div className="flex items-start gap-3">
            <Wand2 className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div>
              <p className="font-medium text-indigo-900">How it works</p>
              <p className="text-sm text-indigo-700 mt-1">
                Every email is uniquely generated for each customer using AI. 
                No two customers receive the same message - it's personal, warm, and authentic.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 py-2">
          {/* Template Name */}
          <div className="grid gap-2">
            <Label htmlFor="name">Template Name</Label>
            <Input
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Welcome Email, Post-Purchase Thank You"
            />
            <p className="text-xs text-gray-500">For your reference only - customers won't see this</p>
          </div>

          {/* Message Type — locked in edit mode: the type is the template's
              slot identity in the 4-card menu; changing it would silently move
              the template between slots. */}
          <div className="grid gap-2">
            <Label htmlFor="type">Message Type</Label>
            {isEdit ? (
              <div className="flex items-center h-10 px-3 rounded-md border bg-gray-50 text-sm text-gray-600">
                {{ thank_you: "🙏 Thank You", review_request: "⭐ Review Request", vip_upsell: "👑 VIP Upsell", custom_offer: "🎁 Custom Offer" }[formData.type] || formData.type}
              </div>
            ) : (
            <Select
              value={formData.type}
              onValueChange={handleTypeChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="thank_you">🙏 Thank You</SelectItem>
                <SelectItem value="review_request">⭐ Review Request</SelectItem>
                <SelectItem value="vip_upsell">👑 VIP Upsell</SelectItem>
                <SelectItem value="custom_offer">🎁 Custom Offer</SelectItem>
              </SelectContent>
            </Select>
            )}
            <div className="bg-gray-50 rounded-md p-3 mt-1">
              <p className="text-sm font-medium text-gray-700">{currentType.title}</p>
              <p className="text-xs text-gray-500 mt-1">{currentType.description}</p>
              <div className="flex items-start gap-2 mt-2 text-xs text-indigo-600">
                <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{currentType.aiExample}</span>
              </div>
            </div>
          </div>

          {/* Trigger */}
          <div className="grid gap-2">
            <Label htmlFor="trigger">When to Send</Label>
            <Select
              value={formData.trigger}
              onValueChange={handleTriggerChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="order_placed">📦 After Order Placed</SelectItem>
                <SelectItem value="order_delivered">🚚 After Delivery</SelectItem>
                <SelectItem value="high_value_purchase">💎 High Value Orders</SelectItem>
                <SelectItem value="repeat_customer">🔄 Repeat Customers</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">{triggerDescriptions[formData.trigger]}</p>
            {/* Soft UX nudge — flagged but not blocking. Combinations like
                "Review Request" + "After Order Placed" usually surprise the
                customer (asks to review a product before it has arrived),
                so we mention the natural fit. Merchant can still ignore it. */}
            {NATURAL_TRIGGER_BY_TYPE[formData.type] &&
              NATURAL_TRIGGER_BY_TYPE[formData.type] !== formData.trigger && (
                <div className="flex items-start gap-2 mt-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Heads up:</strong> {currentType.title} is usually sent “
                    {(() => {
                      const labels = {
                        order_placed: "After Order Placed",
                        order_delivered: "After Delivery",
                        high_value_purchase: "High Value Orders",
                        repeat_customer: "Repeat Customers",
                      };
                      return labels[NATURAL_TRIGGER_BY_TYPE[formData.type]];
                    })()}
                    .” Your choice is fine if intentional.
                  </span>
                </div>
              )}
          </div>

          {/* Subject Line */}
          <div className="grid gap-2">
            <Label htmlFor="subject">Email Subject Line</Label>
            <Input
              id="subject"
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Thank you for your order, {{customer_name}}!"
            />
            <p className="text-xs text-gray-500">
              Use {"{{customer_name}}"} to personalize. Example: "Thanks for shopping with us, Sarah!"
            </p>
          </div>

          {/* AI vs Manual Toggle */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-medium">Email Content</Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setUseAI(true)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    useAI 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI Generated
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setUseAI(false)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    !useAI 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Write My Own
                </button>
              </div>
            </div>

            {useAI ? (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-md p-4 text-center">
                <Wand2 className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
                <p className="font-medium text-indigo-900">AI will write unique content for each customer</p>
                <p className="text-sm text-indigo-700 mt-1">
                  Based on their name, order details, and your store's personality
                </p>
              </div>
            ) : (
              <div>
                <Textarea
                  id="content"
                  className="min-h-[150px]"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={`Dear {{customer_name}},

Thank you for your order! We appreciate your business.

Best regards,
{{shop_name}}`}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Variables: {"{{customer_name}}"}, {"{{order_number}}"}, {"{{shop_name}}"}
                </p>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={isLoading || !shop}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {isEdit ? "Save Changes" : "Create Template"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}